import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import RequestQuoteValidation from "../../Validation/RequestQuoteValidation";
import { useForm } from "react-hook-form"
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Routes } from "../../constants/RouteConstants";
import { getFromStorage, imageExtentionSupport, ImageVideoExtentionSupport, videoExtentionSupport } from "../../utils/Helper";
import { POSTAPI } from "../../config/Api";
import { ApiTypes, StorageConstant, VALIDATION_MESSAGE } from "../../constants/Constants";
import BrandedLoader from "../layout/BrandedLoader";
import { useResourceEverLoaded, useSmartBrandedLoader } from "../layout/useSmartBrandedLoader";
import { toast } from "react-toastify";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { socket } from "../../utils/Socket";
import Select, { components } from "react-select";
import NewAddress, { type NewAddressWizardHandle } from "../../Modal/NewAddress";
import truckIcon from "../../Assets/images/Truck.svg";
import { QUOTE_SUCCESS_LOGO_URL, preloadQuoteSuccessLogo } from "../../utils/quoteSuccessLogo";
import gridIcon from "../../Assets/images/grid-icon.svg";
import listIcon from "../../Assets/images/list-icon.svg";
import "./manual-quote-wizard.scss";
import { clearLegacyManualQuoteDraftStorage, readLegacyManualDraftRecords } from "../../utils/manualQuoteDraftsStorage";
import {
  deleteServerDraftByQuotationId,
  fetchServerDraftRowByQuotationSearch,
  formRowsToQuotesCreatePayload,
  mapServerDraftRowToHydrateParts,
} from "../../utils/quoteDraftServer";
import { invalidatePendingDashboardBootstrapCache } from "../../utils/pendingDashboardBootstrap";
import { isBusinessProfileIncomplete } from "../../utils/pendingDashboardActions";
import { buildSortedShippingDisplayRows } from "../../utils/shippingDisplay";
import { formatAddressListRow } from "../myquotation/view-quotation/helpers";

const parseWizardStepFromQuery = (raw: string | null): 1 | 2 | 3 | null => {
  if (raw === "1" || raw === "2" || raw === "3") return Number(raw) as 1 | 2 | 3;
  return null;
};

interface QuoteInput {
  brand_name: string;
  part_number: string;
  quantity: number;
  description?: string;    // Optional
  serial_number?: string;  // Optional
  model_number?: string;   // Optional
  file_path?: string;
  video_path?: string;
  file_path2?: string;
  /** Upload sequence for admin media display (image1 / image2 / video). */
  media_upload_order?: Array<'image1' | 'image2' | 'video'>;
  /** Client-side original filename (with extension) for display; not sent to quote APIs. */
  file_original_name?: string;
  file_original_name2?: string;
  video_original_name?: string;
  is_other_brand: string;
  other_brand_name: string;
  media_type: string;
  file_limit: string;
  sku?: string;
}

/** Last segment of upload URL/path for showing extension when original name is unknown (e.g. draft hydrate). */
function fileBasenameFromPath(path: string): string {
  if (!path || typeof path !== 'string') return '';
  const segment = path.split('/').pop() || path.split('\\').pop() || path;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function mediaChipLabel(original: string | undefined, serverPath: string | undefined, fallback: string): string {
  const o = String(original ?? '').trim();
  if (o) return o;
  const fromPath = fileBasenameFromPath(String(serverPath ?? '').trim());
  if (fromPath) return fromPath;
  return fallback;
}

/** File extension including dot, from original filename or server path (e.g. `.png`). */
function fileExtensionFromFilename(filename: string): string {
  const base = fileBasenameFromPath(String(filename ?? '').trim());
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot >= base.length - 1) return '';
  return base.slice(dot);
}

/** Chip text: base title (e.g. "Image 1") plus extension when known → `Image 1.png`. */
function manualQuoteMediaChipTitle(
  baseTitle: string,
  originalName: string | undefined,
  serverPath: string | undefined,
): string {
  const ext =
    fileExtensionFromFilename(String(originalName ?? '')) ||
    fileExtensionFromFilename(String(serverPath ?? ''));
  return ext ? `${baseTitle}${ext}` : baseTitle;
}

type MediaUploadKind = 'image1' | 'image2' | 'video';

function withMediaUploadOrder(
  current: unknown,
  kind: MediaUploadKind,
  action: 'add' | 'remove',
): MediaUploadKind[] {
  const prev = Array.isArray(current)
    ? current.filter((k): k is MediaUploadKind => k === 'image1' || k === 'image2' || k === 'video')
    : [];
  const without = prev.filter((k) => k !== kind);
  return action === 'add' ? [...without, kind] : without;
}

/** Review step: show trimmed string or localized N/A placeholder. */
function reviewDisplay(value: unknown, naLabel: string): string {
  const s = String(value ?? "").trim();
  return s || naLabel;
}

interface FormInputs {
  quotes: QuoteInput[];
}

/** True if user has started the manual quote on Data Entry (any row has a meaningful value). */
function hasDataEntryProgress(quotes: any[] | undefined | null): boolean {
  if (!Array.isArray(quotes) || quotes.length === 0) return false;
  const t = (v: any) => String(v ?? "").trim();
  for (const q of quotes) {
    if (t(q?.part_number)) return true;
    if (t(q?.description)) return true;
    if (t(q?.serial_number)) return true;
    if (t(q?.model_number)) return true;
    if (t(q?.sku)) return true;
    const qty = Number(q?.quantity);
    if (Number.isFinite(qty) && qty > 0) return true;
    const brand = t(q?.brand_name);
    if (brand && brand !== "0") return true;
    if (brand === "0" && t(q?.other_brand_name)) return true;
    if (t(q?.file_path) || t(q?.file_path2) || t(q?.video_path)) return true;
  }
  return false;
}

const EMPTY_QUOTE_UI_ROW = {
  brand_name: "",
  part_number: "",
  quantity: "",
  description: "",
  serial_number: "",
  model_number: "",
  sku: "",
  other_brand_name: "",
  file_path: "",
  file_path2: "",
  video_path: "",
  file_original_name: "",
  file_original_name2: "",
  video_original_name: "",
  media_upload_order: [] as MediaUploadKind[],
};

function emptyQuoteFormRow(): QuoteInput {
  return {
    media_type: "one",
    sku: "",
    file_original_name: "",
    file_original_name2: "",
    video_original_name: "",
    brand_name: "",
    part_number: "",
    quantity: "" as unknown as number,
    description: "",
    serial_number: "",
    model_number: "",
    other_brand_name: "",
    file_path: "",
    file_path2: "",
    video_path: "",
    media_upload_order: [],
    is_other_brand: "0",
    file_limit: "",
  };
}

/** Keep display ids 1..n in sync with row order (avoids duplicate keys after delete + add). */
function reindexQuoteUiRows(rows: any[]): any[] {
  return rows.map((row, idx) => ({ ...row, id: String(idx + 1) }));
}

function shiftBrandInputIndices(
  prev: { [key: number]: string },
  removedIndex: number,
): { [key: number]: string } {
  const next: { [key: number]: string } = {};
  Object.keys(prev).forEach((key) => {
    const oldIdx = Number(key);
    if (!Number.isFinite(oldIdx)) return;
    if (oldIdx < removedIndex) next[oldIdx] = prev[oldIdx];
    else if (oldIdx > removedIndex) next[oldIdx - 1] = prev[oldIdx];
  });
  return next;
}

/** After address create, API returns saved `shipping_id` — use it so wizard shipping radios stay in sync. */
function shippingIdFromAddressCreateResponse(created: unknown): string {
  if (created == null || typeof created !== "object") return "";
  const c = created as Record<string, unknown>;
  const raw = c.shipping_id ?? c.shippingId;
  if (raw == null || raw === "") return "";
  const str = String(raw).trim();
  if (!str || str === "0") return "";
  return str;
}

function RequestQuote() {
  const brandList = useMakeRequest()
  const quoteAdd = useMakeRequest()
  const quoteImageVideo = useMakeRequest()
  const quoteDelete = useMakeRequest()
  const Reset = useMakeRequest()
  const [indexId, setIndexId] = useState('0')   // this index for image and video add
  const [deleteIndexId, setDeleteIndexId] = useState('0')  // this index for image and video delete
  const [deleteType, setDeleteType] = useState('')  // this  for  delete  like video and image type
  const [mediaType, setMediaType] = useState('one')
  const [fileposition, setFileposition] = useState('')
  const [deleteImagePosition, setDeleteImageposition] = useState('')
  const [imageimit, setImageLimit] = useState<any>('0')
  const navigate = useNavigate()
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = location;
  const draftQuery = searchParams.get("draft") ?? "";
  const stepQuery = parseWizardStepFromQuery(searchParams.get("step"));
  const profileUser = useSelector((s: any) => s?.ProfileState?.data?.data);

  useEffect(() => {
    if (state?.data) return;
    if (!profileUser) return;
    if (!isBusinessProfileIncomplete(profileUser)) return;
    toast.info(LanguageConstants?.BUSINESS_PROFILE_REQUIRED_TOAST ?? "", { toastId: "biz-profile-quote-gate" });
    navigate("/profile", { replace: true });
  }, [profileUser, state?.data, navigate]);
  const [brandissue, setBrandIssue] = useState(false)
  const addressListShipping = useMakeRequest()
  const shippingListWizard = useMakeRequest()
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [shippingData, setShippingData] = useState<{ address_id: string; shipping_id: string }>({
    address_id: "",
    shipping_id: "",
  });
  const [shippingMeta, setShippingMeta] = useState({
    residence_type: "" as "" | "residential" | "business" | "industrial",
    billing_same: false,
    delivery_instructions: "",
  });
  const [shippingErrors, setShippingErrors] = useState<{ address?: string; shipping?: string }>({});
  /** When user taps "+ New Address", show full address form inline between address list and shipping method (no modal). */
  const [showInlineNewAddressForm, setShowInlineNewAddressForm] = useState(false);

  useEffect(() => {
    if (step !== 2) {
      setShowInlineNewAddressForm(false);
      setInlineAddressBlockingLoad(false);
    }
  }, [step]);

  useEffect(
    () => () => {
      setInlineAddressBlockingLoad(false);
    },
    [],
  );
  const [addressModalKey, setAddressModalKey] = useState(0);
  /** Remount inline first-address form if user ever returns to zero saved addresses. */
  const [firstAddressFormKey, setFirstAddressFormKey] = useState(0);
  const firstAddressWizardRef = useRef<NewAddressWizardHandle | null>(null);
  const shippingListStep2RefetchDone = useRef(false);
  const [draftHydrateLoading, setDraftHydrateLoading] = useState(
    () => Boolean(draftQuery) && !state?.data,
  );
  const [inlineAddressBlockingLoad, setInlineAddressBlockingLoad] = useState(false);
  /** Default list (table) view — same as My Quotation detail page. */
  const [gridView, setGridView] = useState(true);
  const [videoimit, setVideoLimit] = useState<any>('0')
  const [brandInputValues, setBrandInputValues] = useState<{ [key: number]: string }>({})
  /** Ignore react-select's empty input when the menu opens (chevron click). */
  const brandMenuOpeningRef = useRef<Record<number, boolean>>({})
  const OTHER_BRAND_SELECT_VALUE = '__other_brand__'
  const [activeDraftQuotationId, setActiveDraftQuotationId] = useState<string | null>(null);
  const activeDraftQuotationIdRef = useRef<string | null>(null);
  const legacyMigrationAttempted = useRef(false);
  const [uploadingQuoteIndex, setUploadingQuoteIndex] = useState<number | null>(null);

  const [qoute, setQoute]: any = useState([
    {
      id: 1,
      brand_name: "",
      part_number: "",
      quantity: "",
      description: "",
      serial_number: "",
      model_number: "",
      sku: "",
      other_brand_name: "",
      file_path: "",
      file_path2: "",
      video_path: "",
      file_original_name: "",
      file_original_name2: "",
      video_original_name: "",
      media_upload_order: [],
    },
  ]);
  useEffect(() => {
    brandList.makeRequest({ url: ApiConstants?.BrandList, toastStatus: false })
  }, [])

  useEffect(() => {
    preloadQuoteSuccessLogo();
  }, []);

  /* Prefetch address + shipping lists so "Next" to Shipping does not block on a full-page loader. */
  useEffect(() => {
    if (state?.data) return;
    addressListShipping.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
    shippingListWizard.makeRequest({ url: ApiConstants?.ShippingList, toastStatus: false });
  }, [state?.data]);

  /* If user lands on Shipping before prefetch finished (or list was empty), load methods once. */
  useEffect(() => {
    if (state?.data) return;
    if (step !== 2) {
      shippingListStep2RefetchDone.current = false;
      return;
    }
    if (shippingListWizard.loading) return;
    const raw = shippingListWizard?.data?.data;
    if (buildSortedShippingDisplayRows(raw).length > 0) return;
    if (shippingListStep2RefetchDone.current) return;
    shippingListStep2RefetchDone.current = true;
    shippingListWizard.makeRequest({ url: ApiConstants?.ShippingList, toastStatus: false });
  }, [step, state?.data, shippingListWizard?.data?.data, shippingListWizard.loading]);

  /** Pre-select primary delivery address on shipping tab when none is chosen yet. */
  useEffect(() => {
    if (state?.data) return;
    if (step !== 2) return;
    if (String(shippingData.address_id ?? "").trim() !== "") return;

    const rows = addressListShipping?.data?.data?.rows;
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) return;

    const primary =
      list.find(
        (r: any) => Number(r?.is_primary) === 1 || String(r?.is_primary) === "1",
      ) ?? list[0];

    if (primary?.id == null) return;

    setShippingData((s) => ({
      ...s,
      address_id: String(primary.id),
      shipping_id: "",
    }));
  }, [state?.data, step, shippingData.address_id, addressListShipping?.data?.data?.rows]);

  const { register, handleSubmit, formState: { errors }, setValue, watch, getValues, reset, trigger, clearErrors } = useForm<FormInputs>({
    defaultValues: {
      quotes: [
        {
          media_type: "one",
          sku: "",
          file_original_name: "",
          file_original_name2: "",
          video_original_name: "",
        } as QuoteInput,
      ],
    },
    resolver: yupResolver(RequestQuoteValidation),
  });

  const normalizePartNumber = (partNumber: unknown) => String(partNumber ?? "").trim().toLowerCase();
  const partNumberUniqueMessage = "Part Number must be unique in this quotation.";

  const validateNewPartNumbersAreUnique = (quotes: QuoteInput[], existingPartNumbers: unknown[] = []) => {
    const partNumberFieldPaths = quotes.map((_, index) => `quotes.${index}.part_number` as const);
    if (partNumberFieldPaths.length) clearErrors(partNumberFieldPaths);

    const existing = new Set(existingPartNumbers.map(normalizePartNumber).filter(Boolean));
    const seen = new Set<string>();
    const duplicateIndexes = new Set<number>();

    quotes.forEach((quote, index) => {
      const partNumber = normalizePartNumber(quote?.part_number);
      if (!partNumber) return;

      if (existing.has(partNumber) || seen.has(partNumber)) {
        duplicateIndexes.add(index);
      } else {
        seen.add(partNumber);
      }
    });

    if (duplicateIndexes.size) {
      toast.error(partNumberUniqueMessage);
      return false;
    }

    return true;
  };

  const handleAddDiv = () => {
    setImageLimit("0");
    setVideoLimit("0");
    setFileposition("");
    const formQuotes = getValues("quotes") ?? [];
    const nextUi = reindexQuoteUiRows([...qoute, { id: "0", ...EMPTY_QUOTE_UI_ROW }]);
    const nextForm = [...formQuotes, emptyQuoteFormRow()];
    setQoute(nextUi);
    reset({ quotes: nextForm });
  };

  const handleRemoveDiv = (id: any) => {
    const removeIdx = qoute.findIndex((div: any) => String(div.id) === String(id));
    if (removeIdx < 0) return;
    const formQuotes = getValues("quotes") ?? [];
    const nextUi = reindexQuoteUiRows(qoute.filter((_row: any, i: number) => i !== removeIdx));
    const nextForm = formQuotes.filter((_: unknown, i: number) => i !== removeIdx);
    setQoute(nextUi);
    reset({ quotes: nextForm.length > 0 ? nextForm : [emptyQuoteFormRow()] });
    setBrandInputValues((prev) => shiftBrandInputIndices(prev, removeIdx));
  };

  const draftHydrated = useRef(false);
  const lastHydratedDraftKey = useRef<string | null>(null);
  const quoteSubmitSuccessHandled = useRef(false);
  /** Routes upload API response to first image, second image, or video slot. */
  const pendingUploadRef = useRef<{
    index: number;
    kind: 'image1' | 'image2' | 'video';
    originalName: string;
  } | null>(null);

  const applyHydratedDraft = useCallback((draft: {
    quotes: any[];
    shippingData?: { address_id: string; shipping_id: string };
    shippingMeta?: { residence_type: "" | "residential" | "business" | "industrial"; billing_same: boolean; delivery_instructions: string };
    step?: 1 | 2 | 3;
    quotationId?: string | null;
  }) => {
    if (!draft?.quotes?.length) return;
    draftHydrated.current = true;
    const normalizedQuotes = draft.quotes.map((q: any) => {
      const paths = Array.isArray(q?.image_paths)
        ? q.image_paths.map((p: any) => String(p ?? '').trim()).filter(Boolean)
        : [];
      const fp =
        String(q?.file_path ?? '').trim() ||
        (paths[0] ? paths[0] : '');
      const fp2 =
        String(q?.file_path2 ?? '').trim() ||
        (paths[1] ? paths[1] : '');
      return {
        ...q,
        file_path: fp,
        file_path2: fp2,
        file_original_name:
          String(q?.file_original_name ?? '').trim() || (fp ? fileBasenameFromPath(fp) : ''),
        file_original_name2:
          String(q?.file_original_name2 ?? '').trim() || (fp2 ? fileBasenameFromPath(fp2) : ''),
        video_original_name:
          String(q?.video_original_name ?? '').trim() ||
          (String(q?.video_path ?? '').trim() ? fileBasenameFromPath(String(q.video_path)) : ''),
        media_upload_order: Array.isArray(q?.media_upload_order) ? q.media_upload_order : [],
      };
    });
    reset({ quotes: normalizedQuotes });
    const rows = normalizedQuotes.map((q: any, idx: number) => ({
      id: String(idx + 1),
      brand_name: q?.brand_name ?? "",
      part_number: q?.part_number ?? "",
      quantity: q?.quantity ?? "",
      description: q?.description ?? "",
      serial_number: q?.serial_number ?? "",
      model_number: q?.model_number ?? "",
      sku: q?.sku ?? "",
      other_brand_name: q?.other_brand_name ?? "",
      file_path: q?.file_path ?? "",
      file_path2: q?.file_path2 ?? "",
      video_path: q?.video_path ?? "",
      file_original_name: q?.file_original_name ?? '',
      file_original_name2: q?.file_original_name2 ?? '',
      video_original_name: q?.video_original_name ?? '',
      media_upload_order: Array.isArray(q?.media_upload_order) ? q.media_upload_order : [],
    }));
    setQoute(rows);
    const brandRows = brandList?.data?.data;
    if (Array.isArray(brandRows)) {
      const labels: { [key: number]: string } = {};
      rows.forEach((r, index) => {
        const bid = String(r?.brand_name ?? "").trim();
        if (!bid || bid === "0") return;
        const hit = brandRows.find((b: any) => String(b?.id) === bid);
        if (hit?.brands_details?.name) labels[index] = hit.brands_details.name;
      });
      if (Object.keys(labels).length > 0) setBrandInputValues((prev) => ({ ...prev, ...labels }));
    }
    if (draft.shippingData) setShippingData(draft.shippingData);
    if (draft.shippingMeta) setShippingMeta(draft.shippingMeta);
    const hydratedStep = stepQuery ?? draft.step;
    if (hydratedStep === 1 || hydratedStep === 2 || hydratedStep === 3) setStep(hydratedStep);
    if (draft.quotationId != null && String(draft.quotationId).trim() !== "") {
      const qid = String(draft.quotationId);
      setActiveDraftQuotationId(qid);
      activeDraftQuotationIdRef.current = qid;
    }
  }, [reset, stepQuery, brandList?.data?.data]);

  /** After draft hydrate, sync brand select labels when brand list finishes loading. */
  useEffect(() => {
    if (state?.data || !draftHydrated.current) return;
    const brands = brandList?.data?.data;
    if (!Array.isArray(brands) || brands.length === 0) return;
    const formQuotes = getValues("quotes") ?? [];
    setBrandInputValues((prev) => {
      const next = { ...prev };
      formQuotes.forEach((q: any, index: number) => {
        const bid = String(q?.brand_name ?? "").trim();
        if (!bid || bid === "0") return;
        const hit = brands.find((b: any) => String(b?.id) === bid);
        if (hit?.brands_details?.name) next[index] = hit.brands_details.name;
      });
      return next;
    });
  }, [state?.data, brandList?.data?.data, getValues]);

  useEffect(() => {
    if (state?.data) return;

    let cancelled = false;
    void (async () => {
      const fromState = (location.state as { manualDraftId?: string } | null)?.manualDraftId;
      const fromQuery = draftQuery || undefined;
      const draftKey = fromState || fromQuery || null;

      if (draftKey) {
        if (lastHydratedDraftKey.current === draftKey && draftHydrated.current) {
          setDraftHydrateLoading(false);
          return;
        }
        setDraftHydrateLoading(true);
        try {
          const row = await fetchServerDraftRowByQuotationSearch(String(draftKey));
          if (cancelled || !row) return;
          const parts = mapServerDraftRowToHydrateParts(row);
          applyHydratedDraft({
            quotes: parts.quotes,
            shippingData: parts.shippingData,
            shippingMeta: parts.shippingMeta,
            step: parts.step,
            quotationId: parts.quotationId,
          });
          lastHydratedDraftKey.current = draftKey;
          if (!fromQuery && parts.quotationId) {
            setSearchParams({ draft: parts.quotationId }, { replace: true });
          }
        } finally {
          if (!cancelled) setDraftHydrateLoading(false);
        }
        return;
      }

      setDraftHydrateLoading(false);

      if (legacyMigrationAttempted.current) return;
      legacyMigrationAttempted.current = true;
      const legacyList = readLegacyManualDraftRecords();
      if (!legacyList.length) return;
      const best = [...legacyList].sort((a, b) => b.savedAt - a.savedAt)[0];
      const quotes = formRowsToQuotesCreatePayload({ quotes: best.quotes });
      if (!quotes.length) {
        clearLegacyManualQuoteDraftStorage();
        return;
      }
      const res = await POSTAPI(
        ApiConstants.QuotesDraftSave,
        {
          quotes,
          shipping_id: best.shippingData?.shipping_id || null,
          address_id: best.shippingData?.address_id || null,
        },
        false
      );
      if (cancelled) return;
      if (res?.status === true && res?.data?.quotation_id != null) {
        clearLegacyManualQuoteDraftStorage();
        const qid = String(res.data.quotation_id);
        setSearchParams({ draft: qid }, { replace: true });
        invalidatePendingDashboardBootstrapCache();
      }
    })();

    return () => {
      cancelled = true;
      setDraftHydrateLoading(false);
    };
  }, [state?.data, location.state, draftQuery, setSearchParams, applyHydratedDraft]);

  /** Keep current wizard step in URL so language reload preserves active tab (e.g. stays on Review). */
  useEffect(() => {
    if (state?.data) return;
    if (!draftQuery && !activeDraftQuotationIdRef.current) return;
    const currentStepInUrl = searchParams.get("step");
    const nextStep = String(step);
    if (currentStepInUrl === nextStep) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("step", nextStep);
    setSearchParams(nextParams, { replace: true });
  }, [state?.data, draftQuery, step, searchParams, setSearchParams]);

  const buildApiQuotes = (data: any) => formRowsToQuotesCreatePayload(data);

  const validateShippingStep = (): boolean => {
    const errobj: { address?: string; shipping?: string } = {};
    if (!shippingData.shipping_id) {
      errobj.shipping = LanguageConstants?.PLEASE_SELECT_SHIPPING_TYPE;
    }
    if (!shippingData.address_id) {
      errobj.address = LanguageConstants?.PLEASE_SELECR_DELIVER_ADDRESS;
    }
    if (Object.keys(errobj).length > 0) {
      setShippingErrors(errobj);
      return false;
    }
    setShippingErrors({});
    return true;
  };

  const persistDraftToApi = useCallback(async (): Promise<boolean> => {
    if (state?.data) return false;
    const data = getValues();
    if (!hasDataEntryProgress(data?.quotes)) return false;
    const quotes = formRowsToQuotesCreatePayload(data);
    if (!quotes.length) return false;
    const payload: Record<string, unknown> = {
      quotes,
      shipping_id: shippingData.shipping_id || null,
      address_id: shippingData.address_id || null,
    };
    const existingQid = activeDraftQuotationIdRef.current || activeDraftQuotationId;
    if (existingQid) payload.quotation_id = existingQid;
    const res = await POSTAPI(ApiConstants.QuotesDraftSave, payload, false);
    if (res?.status === true && res?.data?.quotation_id != null) {
      const nextQ = String(res.data.quotation_id);
      setActiveDraftQuotationId(nextQ);
      activeDraftQuotationIdRef.current = nextQ;
      invalidatePendingDashboardBootstrapCache();
      return true;
    }
    return false;
  }, [state?.data, shippingData.address_id, shippingData.shipping_id, activeDraftQuotationId, getValues]);

  /** Validates data entry only — server draft is created/updated via Save for Later. */
  const validateStep1ForAdvance = async (): Promise<boolean> => {
    if (brandissue) {
      toast.error(LanguageConstants?.BRAND_NAME_ALREDY);
      return false;
    }
    const ok = await trigger("quotes");
    if (!ok) {
      toast.error(LanguageConstants?.MANUAL_QUOTE_COMPLETE_ITEMS);
      return false;
    }
    if (!validateNewPartNumbersAreUnique(getValues("quotes") ?? [])) {
      return false;
    }
    return true;
  };

  const advanceToShippingStep = useCallback(async (): Promise<boolean> => {
    const ok = await validateStep1ForAdvance();
    if (!ok) return false;
    startTransition(() => setStep(2));
    return true;
  }, [brandissue, trigger]);

  const brandListEverLoaded = useResourceEverLoaded(brandList?.data?.data != null);
  const addressListEverLoaded = useResourceEverLoaded(addressListShipping?.data?.data != null);
  const shippingListEverLoaded = useResourceEverLoaded(shippingListWizard?.data?.data != null);

  const wizardStepRawLoading = useMemo(() => {
    if (state?.data) return false;
    if (step === 1) {
      return (
        (brandList.loading && !brandListEverLoaded) ||
        (draftHydrateLoading && Boolean(draftQuery))
      );
    }
    if (step === 2) {
      return (
        (addressListShipping.loading && !addressListEverLoaded) ||
        (shippingListWizard.loading && !shippingListEverLoaded) ||
        inlineAddressBlockingLoad
      );
    }
    if (step === 3) {
      return (
        (brandList.loading && !brandListEverLoaded) ||
        (addressListShipping.loading &&
          !addressListEverLoaded &&
          Boolean(shippingData.address_id))
      );
    }
    return false;
  }, [
    state?.data,
    step,
    brandList.loading,
    brandListEverLoaded,
    draftHydrateLoading,
    draftQuery,
    addressListShipping.loading,
    addressListEverLoaded,
    shippingListWizard.loading,
    shippingListEverLoaded,
    inlineAddressBlockingLoad,
    shippingData.address_id,
  ]);

  const showWizardBrandedLoader = useSmartBrandedLoader(wizardStepRawLoading);

  const goToWizardStep = async (target: 1 | 2 | 3) => {
    if (isAddProduct || target === step) return;
    if (target < step) {
      startTransition(() => setStep(target));
      return;
    }
    if (target === 2 && step === 1) {
      await advanceToShippingStep();
      return;
    }
    if (target === 3 && step === 1) {
      const ok = await validateStep1ForAdvance();
      if (!ok) return;
      if (!validateShippingStep()) {
        toast.error(LanguageConstants?.MANUAL_QUOTE_COMPLETE_SHIPPING_TAB);
        startTransition(() => setStep(2));
        return;
      }
      startTransition(() => setStep(3));
      return;
    }
    if (target === 3 && step === 2) {
      if (!validateShippingStep()) return;
      startTransition(() => setStep(3));
    }
  };

  const onSubmit = async () => {
    if (state?.data) {
      if (brandissue) {
        toast.error(LanguageConstants?.BRAND_NAME_ALREDY);
        return;
      }
      const ok = await trigger("quotes");
      if (!ok) {
        toast.error(LanguageConstants?.MANUAL_QUOTE_COMPLETE_ITEMS);
        return;
      }
      const data = getValues();
      const existingPendingQuotes = Array.isArray(state?.pendingAnotherProduct?.quotes)
        ? state.pendingAnotherProduct.quotes
        : [];
      const existingPartNumbers = [
        ...(Array.isArray(state?.existingQuotePartNumbers) ? state.existingQuotePartNumbers : []),
        ...existingPendingQuotes.map((quote: any) => quote?.part_number),
      ];
      if (!validateNewPartNumbersAreUnique(data.quotes ?? [], existingPartNumbers)) {
        return;
      }
      const quotes = [...existingPendingQuotes, ...buildApiQuotes(data)];
      navigate(`/view-quotation/${state?.data}`, {
        state: {
          pendingAnotherProduct: { quotes, quotation_id: state?.data },
          pendingQuantityEdits: state?.pendingQuantityEdits,
          pendingDeliveryEdit: state?.pendingDeliveryEdit,
          pendingDeletedItems: state?.pendingDeletedItems,
        },
      });
      return;
    }
    await advanceToShippingStep();
  };

  const handleAddProductBack = () => {
    navigate(`/view-quotation/${state?.data}`, {
      state: {
        pendingAnotherProduct: state?.pendingAnotherProduct,
        pendingQuantityEdits: state?.pendingQuantityEdits,
        pendingDeliveryEdit: state?.pendingDeliveryEdit,
        pendingDeletedItems: state?.pendingDeletedItems,
      },
    });
  };

  const showSaveForLaterNeedEntryHint = useCallback(() => {
    void Swal.fire({
      icon: "info",
      title: LanguageConstants?.SAVE_FOR_LATER_HINT_TITLE ?? "",
      html: `<p class="manual-save-hint__body">${LanguageConstants?.SAVE_FOR_LATER_MIN_ENTRY ?? ""}</p>`,
      confirmButtonText: LanguageConstants?.GOT_IT ?? "OK",
      showCancelButton: true,
      cancelButtonText: LanguageConstants?.CANCEL ?? "Cancel",
      buttonsStyling: false,
      focusConfirm: true,
      didOpen: (popup) => {
        const actions = popup.querySelector(".swal2-actions");
        if (!actions || actions.querySelector(".manual-save-hint__actions-inner")) return;
        const confirmBtn = actions.querySelector(".swal2-confirm");
        const cancelBtn = actions.querySelector(".swal2-cancel");
        if (!confirmBtn || !cancelBtn) return;
        const inner = document.createElement("div");
        inner.className = "manual-save-hint__actions-inner";
        inner.appendChild(cancelBtn);
        inner.appendChild(confirmBtn);
        actions.appendChild(inner);
      },
      customClass: {
        popup: "manual-save-confirm manual-save-hint",
        title: "manual-save-confirm__title",
        htmlContainer: "manual-save-confirm__copy manual-save-hint__html",
        actions: "manual-save-confirm__actions manual-save-hint__actions",
        confirmButton: "manual-save-confirm__btn manual-save-confirm__btn--yes manual-save-hint__ok",
        cancelButton: "manual-save-confirm__btn manual-save-confirm__btn--no manual-save-hint__cancel",
      },
    });
  }, []);

  const commitManualDraftToApi = useCallback(async (): Promise<boolean> => {
    const data = getValues();
    if (!hasDataEntryProgress(data?.quotes)) {
      showSaveForLaterNeedEntryHint();
      return false;
    }
    const saved = await persistDraftToApi();
    if (!saved) {
      toast.error(VALIDATION_MESSAGE?.CATCH_ERROR ?? "");
      return false;
    }
    const qid = activeDraftQuotationIdRef.current;
    if (qid) setSearchParams({ draft: qid }, { replace: true });
    return true;
  }, [getValues, persistDraftToApi, showSaveForLaterNeedEntryHint, setSearchParams]);

  const handleSaveForLater = () => {
    const data = getValues();
    if (!hasDataEntryProgress(data?.quotes)) {
      showSaveForLaterNeedEntryHint();
      return;
    }
    void Swal.fire({
      title: LanguageConstants?.SAVE_FOR_LATER_CONFIRM_TITLE,
      html: LanguageConstants?.SAVE_FOR_LATER_CONFIRM_HTML || LanguageConstants?.SAVE_FOR_LATER_CONFIRM_TEXT,
      showCancelButton: true,
      buttonsStyling: false,
      reverseButtons: true,
      confirmButtonText: LanguageConstants?.YES,
      cancelButtonText: LanguageConstants?.NO,
      focusCancel: false,
      customClass: {
        popup: "manual-save-confirm",
        title: "manual-save-confirm__title",
        htmlContainer: "manual-save-confirm__copy",
        actions: "manual-save-confirm__actions manual-save-confirm__actions--row",
        confirmButton: "manual-save-confirm__btn manual-save-confirm__btn--yes",
        cancelButton: "manual-save-confirm__btn manual-save-confirm__btn--no",
      },
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      if (!(await commitManualDraftToApi())) return;
      toast.success(LanguageConstants?.DRAFT_SAVED_LIST ?? LanguageConstants?.DRAFT_SAVED);
      navigate(Routes.Drafts);
    });
  };

  const handleShippingNext = () => {
    if (showInlineNewAddressForm) return;
    if (addressRowsForShipping.length === 0) {
      firstAddressWizardRef.current?.submitWizard();
      return;
    }
    if (!validateShippingStep()) return;
    startTransition(() => setStep(3));
  };

  const submitFinalQuote = () => {
    if (quoteAdd.loading) return;
    const data = getValues();
    const quotes = formRowsToQuotesCreatePayload(data);
    if (!quotes?.length) {
      toast.error(LanguageConstants?.DATANOTFOUND);
      setStep(1);
      return;
    }
    quoteSubmitSuccessHandled.current = false;
    const payload = {
      shipping_id: shippingData.shipping_id,
      address_id: shippingData.address_id,
      quotes,
    };
    quoteAdd.makeRequest({ payload, url: ApiConstants?.QuotesAdd, toastStatus: false });
  };

  useEffect(() => {
    if (!quoteAdd?.status) return;
    if (quoteAdd?.status && quoteSubmitSuccessHandled.current) return;
    const qid = activeDraftQuotationIdRef.current;
    void (async () => {
      if (quoteAdd?.status && qid) {
        await deleteServerDraftByQuotationId(qid);
        invalidatePendingDashboardBootstrapCache();
      }
    })();
    setActiveDraftQuotationId(null);
    activeDraftQuotationIdRef.current = null;
    const payload = {
      sender_type: "1",
      receiver_id: "1",
    };
    socket.emit("admin_count", payload, async () => {});

    if (quoteAdd?.status) {
      quoteSubmitSuccessHandled.current = true;
      const dynamicQuoteId =
        (quoteAdd as any)?.data?.data?.quotation_id ||
        (quoteAdd as any)?.data?.quotation_id ||
        "—";
      void Swal.fire({
        html: `
          <div class="manual-quote-success-modal">
            <img src="${QUOTE_SUCCESS_LOGO_URL}" alt="Quote N Buy" class="manual-quote-success-modal__logo" width="128" height="128" decoding="async" />
            <h2 class="manual-quote-success-modal__title">${LanguageConstants?.QUOTE_SUCCESS_THANK_YOU || ""}</h2>
            <p class="manual-quote-success-modal__id">Quote ID: ${dynamicQuoteId}</p>
            <p class="manual-quote-success-modal__sub mb-3">${LanguageConstants?.QUOTE_SUCCESS_RECEIVED || ""}</p>
            <p class="manual-quote-success-modal__desc">${LanguageConstants?.QUOTE_SUCCESS_REVIEWING || ""}</p>
          </div>
        `,
        customClass: {
          popup: "manual-quote-success-popup",
          confirmButton: "manual-quote-success-popup__home-btn",
        },
        buttonsStyling: false,
        confirmButtonText: LanguageConstants?.HOME || "Home",
        showConfirmButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        timer: 5000,
        timerProgressBar: true,
      }).then(() => {
        navigate("/");
      });
      return;
    }
    navigate("/my-quotation");
  }, [quoteAdd.status, navigate]);

  /**
   * One file per pick. Allowed: two photos (file_path + file_path2), or one photo + one video, or one video then one photo.
   * Not allowed: two photos + video, two videos, or second photo when video + photo already set.
   */
  const handleImageAndVideo = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setIndexId(String(index));
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    if (quoteImageVideo.loading) {
      input.value = '';
      return;
    }

    const fp = String(watch(`quotes.${index}.file_path`) ?? '').trim();
    const fp2 = String(watch(`quotes.${index}.file_path2`) ?? '').trim();
    const vp = String(watch(`quotes.${index}.video_path`) ?? '').trim();

    const extRaw = file.name.split('.').pop();
    const getextention = extRaw ? String(extRaw).toLowerCase() : '';
    const isVideo = videoExtentionSupport?.includes(getextention);
    const isImage = imageExtentionSupport?.includes(getextention);

    if (!ImageVideoExtentionSupport?.includes(getextention)) {
      input.value = '';
      toast.error(LanguageConstants?.VALIDIMAGE);
      return;
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 25) {
      input.value = '';
      toast.error(LanguageConstants?.IMAGE_VIDEO_SIZE_ERROR ?? 'File size exceeds the maximum limit of 25 MB.');
      return;
    }

    if (isVideo) {
      if (vp) {
        input.value = '';
        toast.error(LanguageConstants?.MAX_VIDEO_LIMIT_ERROR);
        return;
      }
      if (fp && fp2) {
        input.value = '';
        toast.error(LanguageConstants?.IMAAGE_VIDEO);
        return;
      }
      setVideoLimit('1');
      pendingUploadRef.current = { index, kind: 'video', originalName: file.name };
      setUploadingQuoteIndex(index);
      void quoteImageVideo.makeRequest({
        payload: { quote_video: file },
        type: ApiTypes?.POSTAPIFORMDATA,
        url: ApiConstants?.QuotesImage,
        toastStatus: false,
      }).finally(() => setUploadingQuoteIndex(null));
      input.value = '';
      return;
    }

    if (isImage) {
      if (fp && fp2) {
        input.value = '';
        toast.error(LanguageConstants?.IMAAGE_VIDEO);
        return;
      }
      if (vp && fp) {
        input.value = '';
        toast.error(LanguageConstants?.IMAAGE_VIDEO);
        return;
      }
      setImageLimit('1');
      let payload: { quote_image1?: File; quote_image2?: File };
      if (!fp) {
        pendingUploadRef.current = { index, kind: 'image1', originalName: file.name };
        payload = { quote_image1: file };
      } else {
        pendingUploadRef.current = { index, kind: 'image2', originalName: file.name };
        payload = { quote_image2: file };
      }
      setUploadingQuoteIndex(index);
      void quoteImageVideo.makeRequest({
        payload,
        type: ApiTypes?.POSTAPIFORMDATA,
        url: ApiConstants?.QuotesImage,
        toastStatus: false,
      }).finally(() => setUploadingQuoteIndex(null));
      input.value = '';
      return;
    }

    input.value = '';
    toast.error(LanguageConstants?.VALIDIMAGE);
  };

  useEffect(() => {
    if (!quoteImageVideo?.status) return;
    const path = quoteImageVideo?.data?.file_path as string | undefined;
    const pending = pendingUploadRef.current;
    const index = pending?.index ?? parseInt(String(indexId), 10);
    if (!Number.isFinite(index) || index < 0) {
      pendingUploadRef.current = null;
      Reset.reset();
      return;
    }

    const labelFromPending = (p: string | undefined) =>
      mediaChipLabel(pending?.originalName ?? '', p ?? '', '');

    if (pending?.kind === 'video') {
      const label = labelFromPending(path);
      const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'video', 'add');
      setValue(`quotes.${index}.video_path`, path ?? '');
      setValue(`quotes.${index}.video_original_name`, label);
      setValue(`quotes.${index}.media_upload_order`, order);
      setQoute((prev: any) => {
        const n = [...prev];
        if (n[index])
          n[index] = {
            ...n[index],
            video_path: path ?? '',
            video_original_name: label,
            media_upload_order: order,
          };
        return n;
      });
    } else if (pending?.kind === 'image2') {
      const label = labelFromPending(path);
      const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'image2', 'add');
      setValue(`quotes.${index}.file_path2`, path ?? '');
      setValue(`quotes.${index}.file_original_name2`, label);
      setValue(`quotes.${index}.media_upload_order`, order);
      setQoute((prev: any) => {
        const n = [...prev];
        if (n[index])
          n[index] = {
            ...n[index],
            file_path2: path ?? '',
            file_original_name2: label,
            media_upload_order: order,
          };
        return n;
      });
    } else if (pending?.kind === 'image1') {
      const label = labelFromPending(path);
      const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'image1', 'add');
      setValue(`quotes.${index}.file_path`, path ?? '');
      setValue(`quotes.${index}.file_original_name`, label);
      setValue(`quotes.${index}.media_upload_order`, order);
      setQoute((prev: any) => {
        const n = [...prev];
        if (n[index])
          n[index] = {
            ...n[index],
            file_path: path ?? '',
            file_original_name: label,
            media_upload_order: order,
          };
        return n;
      });
    } else {
      const ImageVideo = path?.split('/videos');
      if (ImageVideo && ImageVideo.length >= 2) {
        const label = mediaChipLabel('', path ?? '', '');
        const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'video', 'add');
        setValue(`quotes.${index}.video_path`, path ?? '');
        setValue(`quotes.${index}.video_original_name`, label);
        setValue(`quotes.${index}.media_upload_order`, order);
        setQoute((prev: any) => {
          const n = [...prev];
          if (n[index])
            n[index] = {
              ...n[index],
              video_path: path ?? '',
              video_original_name: label,
              media_upload_order: order,
            };
          return n;
        });
      } else {
        const label = mediaChipLabel('', path ?? '', '');
        const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'image1', 'add');
        setValue(`quotes.${index}.file_path`, path ?? '');
        setValue(`quotes.${index}.file_original_name`, label);
        setValue(`quotes.${index}.media_upload_order`, order);
        setQoute((prev: any) => {
          const n = [...prev];
          if (n[index])
            n[index] = {
              ...n[index],
              file_path: path ?? '',
              file_original_name: label,
              media_upload_order: order,
            };
          return n;
        });
      }
    }
    pendingUploadRef.current = null;
    Reset.reset();
  }, [quoteImageVideo?.status, indexId]);


  useEffect(() => {
    if (!quoteDelete?.status) return;
    if (deleteType == "image") {
      const index = parseInt(String(deleteIndexId), 10);
      if (deleteImagePosition == "one") {
        const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'image1', 'remove');
        setValue(`quotes.${index}.file_path`, "");
        setValue(`quotes.${index}.file_original_name`, "");
        setValue(`quotes.${index}.media_upload_order`, order);
        setQoute((prev: any) => {
          const n = [...prev];
          if (n[index]) n[index] = { ...n[index], file_path: "", file_original_name: "", media_upload_order: order };
          return n;
        });
      } else if (deleteImagePosition == "two") {
        const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'image2', 'remove');
        setValue(`quotes.${index}.file_path2`, "");
        setValue(`quotes.${index}.file_original_name2`, "");
        setValue(`quotes.${index}.media_upload_order`, order);
        setQoute((prev: any) => {
          const n = [...prev];
          if (n[index]) n[index] = { ...n[index], file_path2: "", file_original_name2: "", media_upload_order: order };
          return n;
        });
      }
    } else {
      const index = parseInt(deleteIndexId);
      const order = withMediaUploadOrder(getValues(`quotes.${index}.media_upload_order`), 'video', 'remove');
      setValue(`quotes.${index}.video_path`, "");
      setValue(`quotes.${index}.video_original_name`, "");
      setValue(`quotes.${index}.media_upload_order`, order);
      setQoute((prev: any) => {
        const n = [...prev];
        if (n[index]) n[index] = { ...n[index], video_path: "", video_original_name: "", media_upload_order: order };
        return n;
      });
    }
    quoteDelete.reset();
  }, [deleteIndexId, quoteDelete?.status]);


  const handelDeleteImageVideo = (e: any, index: any, type: any, position: any) => {
    setDeleteIndexId(index);
    setDeleteImageposition(position);
    setDeleteType(type);
    if (position == "one" || position == "two") {
      setImageLimit("0");
      setFileposition("");
    }
    if (type == "video") {
      setVideoLimit("0");
    }
    quoteDelete.makeRequest({
      payload: { file_path: e },
      type: ApiTypes?.POST,
      url: ApiConstants?.QuotesImageRemove,
      toastStatus: false,
    });
  };

  const handelImageVideo = (e: any, i: any) => {
    setMediaType(e);
    setValue(`quotes.${i}.media_type`, e);
    setValue(`quotes.${i}.file_path`, "");
    setValue(`quotes.${i}.file_path2`, "");
    setValue(`quotes.${i}.video_path`, "");
    setValue(`quotes.${i}.file_original_name`, "");
    setValue(`quotes.${i}.file_original_name2`, "");
    setValue(`quotes.${i}.video_original_name`, "");
    setValue(`quotes.${i}.media_upload_order`, []);
    setQoute((prev: any) => {
      const n = [...prev];
      if (n[i])
        n[i] = {
          ...n[i],
          file_path: "",
          file_path2: "",
          video_path: "",
          file_original_name: "",
          file_original_name2: "",
          video_original_name: "",
          media_upload_order: [],
        };
      return n;
    });
  };

  const handelOtherBrands = (e: any) => {
    const FindData = brandList?.data?.data?.find((res: any) => res?.brands_details?.name == e)
    if (FindData?.id) {
      toast.error('Brand name already exists.')
      return setBrandIssue(true)
    } else {
      return setBrandIssue(false)
    }
  }

  /** Cleared custom brand text → show searchable brand dropdown again. */
  const revertOtherBrandRowToSelect = (index: number) => {
    setQoute((prev: any[]) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], brand_name: "", other_brand_name: "" };
      }
      return next;
    });
    setValue(`quotes.${index}.brand_name`, "");
    setValue(`quotes.${index}.other_brand_name`, "");
    setBrandInputValues((prev) => ({ ...prev, [index]: "" }));
    setBrandIssue(false);
  };

  const handleOtherBrandNameChange = (index: number, value: string) => {
    handelOtherBrands(value);
    if (!String(value).trim()) {
      revertOtherBrandRowToSelect(index);
      return;
    }
    setValue(`quotes.${index}.brand_name`, '0');
    setValue(`quotes.${index}.other_brand_name`, value);
    setQoute((prev: any[]) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], brand_name: '0', other_brand_name: value };
      }
      return next;
    });
  };

  /** Free-text brand while keeping the searchable dropdown available. */
  const applyCustomBrandTyping = (index: number, value: string) => {
    handleOtherBrandNameChange(index, value);
  };

  const findBrandByName = (name: string) => {
    const needle = String(name ?? '').trim().toLowerCase();
    if (!needle) return null;
    return (
      brandList?.data?.data?.find(
        (res: any) => res?.brands_details?.name?.toLowerCase() === needle,
      ) ?? null
    );
  };

  const brandSelectStyles = {
    control: (base: Record<string, unknown>) => ({
      ...base,
      width: '160px',
      minHeight: '38px',
      height: '38px',
    }),
    valueContainer: (base: Record<string, unknown>) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
    }),
    input: (base: Record<string, unknown>) => ({
      ...base,
      margin: '0px',
      padding: '0px',
    }),
    indicatorsContainer: (base: Record<string, unknown>) => ({
      ...base,
      height: '36px',
    }),
    menuPortal: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    placeholder: (base: Record<string, unknown>) => ({
      ...base,
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
  };

  const brandSelectOptions =
    brandList?.data?.data?.map((res: any) => ({
      value: String(res?.id),
      label: res?.brands_details?.name,
    })) || [];

  /** Match while typing; if nothing matches, hide options (custom brand via Enter/blur). */
  const filterBrandSelectOption = (
    option: { label?: string },
    inputValue: string,
  ): boolean => {
    const needle = String(inputValue ?? '').trim().toLowerCase();
    if (!needle) return true;
    return String(option.label ?? '').toLowerCase().includes(needle);
  };

  const brandInputHasListMatches = (inputValue: string): boolean => {
    const needle = String(inputValue ?? '').trim().toLowerCase();
    if (!needle) return false;
    return brandSelectOptions.some((o: { label?: string }) =>
      String(o.label ?? '').toLowerCase().includes(needle),
    );
  };

  /** Hide the dropdown shell when the typed brand has no matches. */
  const BrandSelectMenu = (props: any) => {
    if (!props.options?.length) return null;
    return <components.Menu {...props} />;
  };

  const getBrandSelectValue = (index: number) => {
    const brandId = watch(`quotes.${index}.brand_name`);
    if (brandId === '0') {
      const other = String(watch(`quotes.${index}.other_brand_name`) || '').trim();
      if (other) {
        return { value: OTHER_BRAND_SELECT_VALUE, label: other };
      }
      return null;
    }
    if (!brandId) return null;
    return brandSelectOptions.find((o: { value: string }) => o.value === String(brandId)) ?? null;
  };

  // Handle brand input change for searchable select
  const handleBrandInputChange = (inputValue: string, index: number) => {
    // Store the input value to check later on blur/menu close
    setBrandInputValues(prev => ({ ...prev, [index]: inputValue }))
  }

  // Handle brand selection from dropdown
  const handleBrandSelect = (selectedOption: any, index: number) => {
    if (selectedOption) {
      if (selectedOption.value === OTHER_BRAND_SELECT_VALUE) {
        return;
      }
      if (selectedOption.value === '0') {
        // User selected "Other Brand"
        const updatedQoutes = [...qoute];
        updatedQoutes[index].brand_name = '0';
        setQoute(updatedQoutes);
        setValue(`quotes.${index}.brand_name`, '0');
        setValue(`quotes.${index}.other_brand_name`, '');
        setBrandInputValues(prev => ({ ...prev, [index]: '' }))
      } else {
        // User selected a brand from list
        const updatedQoutes = [...qoute];
        updatedQoutes[index].brand_name = selectedOption.value;
        updatedQoutes[index].other_brand_name = '';
        setQoute(updatedQoutes);
        setValue(`quotes.${index}.brand_name`, selectedOption.value);
        setValue(`quotes.${index}.other_brand_name`, '');
        setBrandIssue(false);
        setBrandInputValues((prev) => ({ ...prev, [index]: '' }));
      }
    } else {
      // Clear selection
      const updatedQoutes = [...qoute];
      updatedQoutes[index].brand_name = '';
      updatedQoutes[index].other_brand_name = '';
      setQoute(updatedQoutes);
      setValue(`quotes.${index}.brand_name`, '');
      setValue(`quotes.${index}.other_brand_name`, '');
      setBrandInputValues((prev) => ({ ...prev, [index]: '' }));
    }
  }

  // Check if typed value matches a brand and handle accordingly
  const checkAndHandleBrandInput = (index: number) => {
    const brandId = watch(`quotes.${index}.brand_name`);
    if (brandId && brandId !== '0') {
      setBrandInputValues((prev) => ({ ...prev, [index]: '' }));
      return;
    }
    if (brandId === '0') {
      handelOtherBrands(watch(`quotes.${index}.other_brand_name`) || '');
      return;
    }

    const inputValue = (brandInputValues[index] || '').trim();
    if (!inputValue) return;

    const matchedBrand = findBrandByName(inputValue);
    if (matchedBrand) {
      const updatedQoutes = [...qoute];
      updatedQoutes[index].brand_name = String(matchedBrand.id);
      updatedQoutes[index].other_brand_name = '';
      setQoute(updatedQoutes);
      setValue(`quotes.${index}.brand_name`, String(matchedBrand.id));
      setValue(`quotes.${index}.other_brand_name`, '');
      setBrandIssue(false);
      setBrandInputValues((prev) => ({ ...prev, [index]: matchedBrand.brands_details.name }));
      return;
    }

    applyCustomBrandTyping(index, inputValue);
    setBrandInputValues((prev) => ({ ...prev, [index]: '' }));
  }

  const commitBrandInputOnEnter = (
    event: React.KeyboardEvent,
    index: number,
  ) => {
    if (event.key !== 'Enter') return;

    const inputValue = String(brandInputValues[index] ?? '').trim();
    if (!inputValue) return;

    // Matching brands in the list: let react-select select the highlighted option.
    if (brandInputHasListMatches(inputValue)) return;

    event.preventDefault();
    event.stopPropagation();
    checkAndHandleBrandInput(index);
  };

  // Handle blur event - check if typed value should become "other brand" or match a brand
  const handleBrandBlur = (index: number) => {
    checkAndHandleBrandInput(index);
  }

  // Handle menu close - check if typed value should become "other brand" or match a brand
  const handleMenuClose = (index: number) => {
    setTimeout(() => {
      const brandId = watch(`quotes.${index}.brand_name`);
      if (brandId && brandId !== '0') {
        setBrandInputValues((prev) => ({ ...prev, [index]: '' }));
        return;
      }
      checkAndHandleBrandInput(index);
    }, 100);
  }

  const handelNavigateHome = ()=>{
        navigate('/', { state: { data: {modelShow:true} } })
  }

  const isAddProduct = Boolean(state?.data);
  const wizardStep = isAddProduct ? 1 : step;

  const brandLabelForRow = (row: any) => {
    if (!row) return "";
    if (String(row.brand_name) === "0") return row.other_brand_name || LanguageConstants?.OTHER_BRAND;
    const id = String(row.brand_name);
    const b = brandList?.data?.data?.find((res: any) => String(res?.id) === id);
    return b?.brands_details?.name || id;
  };

  const truncateBrandLabel = (label: string, maxWords = 6) => {
    const text = String(label ?? "").trim();
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(" ")}...`;
  };

  const addressRowsForShipping = addressListShipping?.data?.data?.rows || [];
  const selectedAddressRow = addressRowsForShipping.find(
    (r: any) => String(r?.id) === String(shippingData.address_id),
  );
  const wizardShippingRows = useMemo(
    () => buildSortedShippingDisplayRows(shippingListWizard?.data?.data),
    [shippingListWizard?.data?.data],
  );
  const selectedShippingLabel = useMemo(() => {
    const sid = String(shippingData.shipping_id ?? "").trim();
    if (!sid) return "—";
    const byRef = wizardShippingRows.find((r) => r.refId === sid);
    if (byRef) return byRef.label;
    /* Legacy drafts may have stored ship_method.id instead of ref_id */
    const byMethod = wizardShippingRows.find((r) => r.methodId === sid);
    return byMethod?.label ?? "—";
  }, [wizardShippingRows, shippingData.shipping_id]);

  const deliveryInstructionsFromAddressRow = (row: any | undefined) => {
    if (!row) return "";
    return String(row.instructions ?? row.delivery_instructions ?? "").trim();
  };

  /** Sync quote "delivery instructions" from the saved address when selection or list data changes (not on every keystroke). */
  const selectedAddressInstructionsKey = selectedAddressRow
    ? `${String(selectedAddressRow.id)}:${deliveryInstructionsFromAddressRow(selectedAddressRow)}`
    : "";
  useEffect(() => {
    if (isAddProduct || step !== 2) return;
    const instr = deliveryInstructionsFromAddressRow(selectedAddressRow);
    setShippingMeta((m) => ({ ...m, delivery_instructions: instr }));
  }, [isAddProduct, step, shippingData.address_id, selectedAddressInstructionsKey]);

  const initialLoactionTypeForModal =
    shippingMeta.residence_type === "residential"
      ? "1"
      : shippingMeta.residence_type === "business"
        ? "2"
        : shippingMeta.residence_type === "industrial"
          ? "3"
          : undefined;

  /** Residence / billing flags live on the saved address (set in New Address modal), not on the shipping tab. */
  const residenceLabelFromAddressRow = (row: any): string | null => {
    if (!row) return null;
    const t = String(row.loaction_type ?? row.location_type ?? "").trim();
    if (t === "1") return LanguageConstants?.RESIDENTIAL;
    if (t === "2") return LanguageConstants?.LABORAL;
    if (t === "3") return LanguageConstants?.INDUSTRIAL;
    return null;
  };
  const addressBillingSameAsShipping = (row: any): boolean =>
    Number(row?.billing_same_as_shipping) === 1 ||
    row?.billing_same_as_shipping === true ||
    String(row?.billing_same_as_shipping) === "1";

  const reviewResidenceLabel = residenceLabelFromAddressRow(selectedAddressRow);
  const reviewBillingSame = addressBillingSameAsShipping(selectedAddressRow);

  const reviewQuotes = watch("quotes") || [];
  const reviewHeadline = (row: any) => {
    const brand = brandLabelForRow(row);
    return String(brand || "").trim() || row?.part_number || "—";
  };

  const showSubmitLoader = quoteAdd.loading;
  const showWizardShellLoader = showWizardBrandedLoader || showSubmitLoader;

  return (
    <>
      <main>
        <div className="head-space">
          <div className="inner-content">
            <div className="container">
              <div className="manual-quote-page">
                <div className={`manual-quote-wizard${isAddProduct ? " manual-quote-wizard--compact" : ""}`}>
                  <div className="manual-quote-wizard__shell">
                    <BrandedLoader show={showWizardShellLoader} scope="local" />
                    <div
                      className={`manual-quote-wizard__top${!isAddProduct ? " manual-quote-wizard__top--with-title" : ""}`}
                    >
                      {!isAddProduct ? (
                        <div className="manual-quote-wizard__top-start">
                          {step === 1 ? (
                            <button type="button" className="manual-quote-wizard__back" onClick={() => handelNavigateHome()}>
                              <i className="icon-chev-right"></i> {LanguageConstants?.QUOTATIONFORM}
                            </button>
                          ) : (
                            <h2 className="manual-quote-wizard__page-title">{LanguageConstants?.QUOTATIONFORM}</h2>
                          )}
                        </div>
                      ) : (isAddProduct || step === 1) ? (
                        <button type="button" className="manual-quote-wizard__back" onClick={() => handelNavigateHome()}>
                          <i className="icon-chev-right"></i> {LanguageConstants?.QUOTATIONFORM}
                        </button>
                      ) : null}
                      {!isAddProduct ? (
                        <div className="manual-quote-wizard__stepper-wrap">
                          <div className="rq-stepper-static" aria-label="Request quote steps">
                            <button
                              type="button"
                              className={`rq-stepper-static__item rq-stepper-static__trigger${wizardStep > 1 ? " rq-stepper-static__item--complete" : ""}${wizardStep === 1 ? " rq-stepper-static__item--active" : ""}`}
                              onClick={() => void goToWizardStep(1)}
                            >
                              <span className="rq-stepper-static__line" aria-hidden />
                              <span className="rq-stepper-static__dot" aria-hidden>
                                {wizardStep >= 1 ? <i className="icon-tick-2" /> : null}
                              </span>
                              <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_DATA_ENTRY}</span>
                            </button>
                            <button
                              type="button"
                              className={`rq-stepper-static__item rq-stepper-static__trigger${wizardStep > 2 ? " rq-stepper-static__item--complete" : ""}${wizardStep === 2 ? " rq-stepper-static__item--active" : ""}`}
                              onClick={() => void goToWizardStep(2)}
                            >
                              <span className="rq-stepper-static__line" aria-hidden />
                              <span className="rq-stepper-static__dot" aria-hidden>
                                {wizardStep >= 2 ? <i className="icon-tick-2" /> : null}
                              </span>
                              <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_SHIPPING}</span>
                            </button>
                            <button
                              type="button"
                              className={`rq-stepper-static__item rq-stepper-static__trigger${wizardStep === 3 ? " rq-stepper-static__item--active rq-stepper-static__item--complete" : ""}`}
                              onClick={() => void goToWizardStep(3)}
                            >
                              <span className="rq-stepper-static__line" aria-hidden />
                              <span className="rq-stepper-static__dot" aria-hidden>
                                {wizardStep >= 3 ? <i className="icon-tick-2" /> : null}
                              </span>
                              <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_REVIEW}</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <h1 className="sec-ttl text-capitalize mb-0 ms-auto">{LanguageConstants?.QUOTATIONFORM}</h1>
                      )}
                      {!isAddProduct ? (
                        <div
                          className={`manual-quote-wizard__top-spacer${step === 3 ? " d-flex justify-content-end align-items-center w-100" : ""}`}
                          aria-hidden={step === 3 ? undefined : true}
                        >
                          {step === 3 ? (
                            <div className="manual-quote-review__toggle mb-0">
                              <button
                                type="button"
                                className="manual-quote-review__toggle-btn is-active"
                                onClick={() => setGridView((prev) => !prev)}
                              >
                                <span className="manual-quote-review__icon" aria-hidden>
                                  <img src={gridView ? gridIcon : listIcon} alt="" />
                                </span>
                                {gridView
                                  ? LanguageConstants?.GRID_VIEW
                                  : LanguageConstants?.LIST_VIEW}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                {(isAddProduct || step === 1) && (
                <form onSubmit={handleSubmit(onSubmit)} className="inner-height d-flex flex-column">
                      <div className="manual-quote-table-wrap">
                        <table className="table manual-quote-table">
                          <thead>
                            <tr>
                              {qoute?.length > 1 ? <th className="manual-quote-table__del" aria-label="remove" /> : null}
                              <th className="manual-quote-table__item-num">{LanguageConstants?.Item}</th>
                              <th><span className="req">*</span> {LanguageConstants?.BRANDNAME}</th>
                              <th><span className="req">*</span> {LanguageConstants?.PART_NUMBER_HASH}</th>
                              <th><span className="req">*</span> {LanguageConstants?.QTY_SHORT}</th>
                              <th><span className="req">*</span> {LanguageConstants?.Description}</th>
                              <th>{LanguageConstants?.SERIALNUMBER}</th>
                              <th>{LanguageConstants?.MODELNUMBER}</th>
                              <th>{LanguageConstants?.SKU_LABEL}</th>
                              <th>{LanguageConstants?.UPLOAD_IMAGES}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {qoute?.length > 0 && qoute.map((res: any, i: number) => (
                              <tr key={`manual-quote-row-${i}`}>
                                {qoute?.length > 1 ? (
                                  <td className="manual-quote-table__del">
                                    <div className="manual-quote-table__input-row">
                                      <button
                                      type="button"
                                      className="action-btn del"
                                      aria-label={LanguageConstants?.REMOVE}
                                      onClick={() => handleRemoveDiv(res?.id)}
                                    >
                                      <i className="icon-delete-2" aria-hidden />
                                      </button>
                                    </div>
                                  </td>
                                ) : null}
                                <td className="manual-quote-table__item-num">
                                  <div className="manual-quote-table__input-row">
                                    {(state?.quotes_length ? Number(state.quotes_length) : 0) + i + 1}
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                      <div className="com-input-wrap">
                                        <Select
                                          styles={{
                                            ...brandSelectStyles,
                                            control: (base) => ({
                                              ...brandSelectStyles.control(base),
                                              borderColor: errors?.quotes?.[i]?.brand_name
                                                ? '#dc3545'
                                                : '#ced4da',
                                            }),
                                          }}
                                          placeholder={LanguageConstants?.SELECTBRAND}
                                          isSearchable
                                          isClearable={false}
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          className="manual-quote-menu"
                                          classNamePrefix="manual-quote"
                                          options={brandSelectOptions}
                                          formatOptionLabel={(option: { label?: string }) =>
                                            truncateBrandLabel(option?.label ?? '', 6)
                                          }
                                          value={getBrandSelectValue(i)}
                                          components={{ Menu: BrandSelectMenu }}
                                          onMenuOpen={() => {
                                            brandMenuOpeningRef.current[i] = true;
                                          }}
                                          onInputChange={(inputValue, meta) => {
                                            if (meta.action !== 'input-change') return;
                                            const rowBrandId = watch(`quotes.${i}.brand_name`);
                                            if (rowBrandId === '0') {
                                              if (
                                                !inputValue.trim() &&
                                                brandMenuOpeningRef.current[i]
                                              ) {
                                                brandMenuOpeningRef.current[i] = false;
                                                return;
                                              }
                                              brandMenuOpeningRef.current[i] = false;
                                              applyCustomBrandTyping(i, inputValue);
                                              return;
                                            }
                                            if (!inputValue.trim()) {
                                              revertOtherBrandRowToSelect(i);
                                              return;
                                            }
                                            if (rowBrandId && rowBrandId !== '0') {
                                              const updatedQoutes = [...qoute];
                                              updatedQoutes[i].brand_name = '';
                                              updatedQoutes[i].other_brand_name = '';
                                              setQoute(updatedQoutes);
                                              setValue(`quotes.${i}.brand_name`, '');
                                              setValue(`quotes.${i}.other_brand_name`, '');
                                            }
                                            handleBrandInputChange(inputValue, i);
                                          }}
                                          onChange={(selectedOption) => handleBrandSelect(selectedOption, i)}
                                          onBlur={() => handleBrandBlur(i)}
                                          onKeyDown={(event) => commitBrandInputOnEnter(event, i)}
                                          onMenuClose={() => handleMenuClose(i)}
                                          filterOption={filterBrandSelectOption}
                                          noOptionsMessage={() => null}
                                        />
                                        <input type="hidden" {...register(`quotes.${i}.brand_name`)} />
                                        <input type="hidden" {...register(`quotes.${i}.other_brand_name`)} />
                                      </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.brand_name ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.brand_name?.message}</p>
                                      ) : null}
                                      {errors?.quotes?.[i]?.other_brand_name ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.other_brand_name?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input type="text" className="form-control large-2" placeholder={`${LanguageConstants?.ENTER} ${LanguageConstants?.PART_NUMBER_HASH}`}
                                      {...register(`quotes.${i}.part_number`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].part_number = event.target.value; // Update the specific quote's part_number
                                          setQoute(updatedQoutes); // Set the updated state
                                        }
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.part_number ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.part_number?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input type="text" className="form-control"
                                      placeholder={LanguageConstants?.QTY_SHORT}
                                      {...register(`quotes.${i}.quantity`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].quantity = event.target.value; // Update the specific quote's part_number
                                          setQoute(updatedQoutes); // Set the updated state
                                        }
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.quantity ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.quantity?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input type="text" className="form-control large" placeholder={`${LanguageConstants?.ENTERDESCIPTION}`}
                                      {...register(`quotes.${i}.description`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].description = event.target.value; // Update the specific quote's part_number
                                          setQoute(updatedQoutes); // Set the updated state
                                        }
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.description ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.description?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input type="text" className="form-control large-2 " placeholder={`${LanguageConstants?.OPTIONAL_MARK_FIELD}`}
                                      {...register(`quotes.${i}.serial_number`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].serial_number = event.target.value; // Update the specific quote's part_number
                                          setQoute(updatedQoutes); // Set the updated state
                                        }
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.serial_number ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.serial_number?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input type="text" className="form-control large-2" placeholder={`${LanguageConstants?.OPTIONAL_MARK_FIELD}`}
                                      {...register(`quotes.${i}.model_number`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].model_number = event.target.value; // Update the specific quote's part_number
                                          setQoute(updatedQoutes); // Set the updated state
                                        }
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.model_number ? (
                                        <p className="input-error mb-0">{errors.quotes[i]?.model_number?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="manual-quote-table__field-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row">
                                  <div className="com-input-wrap">
                                    <input
                                      type="text"
                                      className="form-control large-3"
                                      placeholder={`${LanguageConstants?.OPTIONAL_MARK_FIELD}`}
                                      {...register(`quotes.${i}.sku`, {
                                        onChange: (event) => {
                                          const updatedQoutes = [...qoute];
                                          updatedQoutes[i].sku = event.target.value;
                                          setQoute(updatedQoutes);
                                        },
                                      })}
                                    />
                                  </div>
                                    </div>
                                    <div className="manual-quote-table__error-slot">
                                      {errors?.quotes?.[i]?.sku ? (
                                        <p className="input-error mb-0">{(errors.quotes[i] as any)?.sku?.message}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                {/* <td>
                                  <div className="com-input-wrap">
                                    <select
                                      className="form-select"
                                      aria-label="Default select example"
                                      onChange={(e) => handelImageVideo(e?.target?.value, i)}
                                    >
                                      <option value='one'>{LanguageConstants?.ONEBYONE}</option>
                                      <option value='two' >{LanguageConstants?.TWOBYTWO}</option>
                                    </select>
                                  </div>
                                </td> */}
                                <td className="manual-quote-upload-cell">
                                  <div className="manual-quote-table__field">
                                    <div className="manual-quote-table__input-row manual-quote-table__input-row--upload">
                                      <div className="upload-btns upload-btns--primary">
                                    <label className={`upload-single${uploadingQuoteIndex === i ? " is-uploading" : ""}`}>
                                      <>
                                        <i className="icon-upload" />
                                        <input
                                          type="file"
                                          className="d-none"
                                          accept="image/*,video/*"
                                          disabled={uploadingQuoteIndex === i}
                                          onChange={(e) => handleImageAndVideo(e, i)}
                                        />
                                      </>
                                    </label>

                                    {/* upload file name area — only when uploading or at least one file/video is present */}
                                    {uploadingQuoteIndex === i ||
                                    watch(`quotes.${i}.file_path`) ||
                                    watch(`quotes.${i}.file_path2`) ||
                                    watch(`quotes.${i}.video_path`) ? (
                                      <div className="d-flex flex-row flex-wrap align-items-center justify-content-center gap-1">
                                        {uploadingQuoteIndex === i ? (
                                          <div className="manual-quote-upload-names">
                                            <span className="manual-quote-upload-loader mx-auto" role="status" aria-live="polite">
                                              <span className="spinner-border spinner-border-sm" aria-hidden />
                                              {/* <span>{(LanguageConstants as any)?.UPLOADING ?? "Uploading..."}</span> */}
                                            </span>
                                          </div>
                                        ) : null}
                                        {watch(`quotes.${i}.file_path`) ? (
                                          <div className="manual-quote-upload-names">
                                            <span className="manual-quote-file-chip">
                                              <span className="manual-quote-file-chip__text" title={mediaChipLabel(watch(`quotes.${i}.file_original_name`), watch(`quotes.${i}.file_path`), LanguageConstants?.MANUAL_QUOTE_CHIP_IMAGE_1 ?? '')}>
                                                {manualQuoteMediaChipTitle(LanguageConstants?.MANUAL_QUOTE_CHIP_IMAGE_1 ?? 'Image 1', watch(`quotes.${i}.file_original_name`), watch(`quotes.${i}.file_path`))}
                                              </span>
                                              <button
                                                type="button"
                                                className="action-btn del"
                                                aria-label={LanguageConstants?.REMOVE}
                                                onClick={() =>
                                                  handelDeleteImageVideo(watch(`quotes.${i}.file_path`), i, "image", "one")
                                                }
                                              >
                                                <i className="icon-delete-2" aria-hidden />
                                              </button>
                                            </span>
                                          </div>
                                        ) : null}
                                        {watch(`quotes.${i}.file_path2`) ? (
                                          <div className="manual-quote-upload-names">
                                            <span className="manual-quote-file-chip">
                                              <span className="manual-quote-file-chip__text" title={mediaChipLabel(watch(`quotes.${i}.file_original_name2`), watch(`quotes.${i}.file_path2`), LanguageConstants?.MANUAL_QUOTE_CHIP_IMAGE_2 ?? '')}>
                                                {manualQuoteMediaChipTitle(LanguageConstants?.MANUAL_QUOTE_CHIP_IMAGE_2 ?? 'Image 2', watch(`quotes.${i}.file_original_name2`), watch(`quotes.${i}.file_path2`))}
                                              </span>
                                              <button
                                                type="button"
                                                className="action-btn del"
                                                aria-label={LanguageConstants?.REMOVE}
                                                onClick={() =>
                                                  handelDeleteImageVideo(watch(`quotes.${i}.file_path2`), i, "image", "two")
                                                }
                                              >
                                                <i className="icon-delete-2" aria-hidden />
                                              </button>
                                            </span>
                                          </div>
                                        ) : null}
                                        {watch(`quotes.${i}.video_path`) ? (
                                          <div className="manual-quote-upload-names">
                                            <span className="manual-quote-file-chip">
                                              <span className="manual-quote-file-chip__text" title={mediaChipLabel(watch(`quotes.${i}.video_original_name`), watch(`quotes.${i}.video_path`), LanguageConstants?.MANUAL_QUOTE_CHIP_VIDEO ?? '')}>
                                                {manualQuoteMediaChipTitle(LanguageConstants?.MANUAL_QUOTE_CHIP_VIDEO ?? 'Video', watch(`quotes.${i}.video_original_name`), watch(`quotes.${i}.video_path`))}
                                              </span>
                                              <button
                                                type="button"
                                                className="action-btn del"
                                                aria-label={LanguageConstants?.REMOVE}
                                                onClick={() =>
                                                  handelDeleteImageVideo(watch(`quotes.${i}.video_path`), i, "video", "one")
                                                }
                                              >
                                                <i className="icon-delete-2" aria-hidden />
                                              </button>
                                            </span>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="manual-quote-footer manual-quote-footer--step mt-auto">
                        <button type="button" className="manual-quote-btn manual-quote-btn--ghost com-btn rounded-pill me-auto" onClick={() => handleAddDiv()}>
                          <i className="icon-add-circle-2" aria-hidden />
                          {LanguageConstants?.ADD_ITEM_PLUS}
                        </button>
                        {!isAddProduct ? (
                          <>
                            <button
                              type="button"
                              className="manual-quote-btn manual-quote-btn--ghost com-btn rounded-pill ms-auto"
                              onClick={handleSaveForLater}
                            >
                              {LanguageConstants?.SAVE_FOR_LATER}
                            </button>
                            <button type="submit" className="manual-quote-btn com-btn rounded-pill">
                              {LanguageConstants?.NEXT}
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill" onClick={handleAddProductBack}>
                              {LanguageConstants?.BACK}
                            </button>
                            <button type="submit" className="manual-quote-btn com-btn rounded-pill">
                              {LanguageConstants?.CONFIRMA}
                            </button>
                          </>
                        )}
                      </div>
                </form>
                )}

                {!isAddProduct && step === 2 && (
                  <div className="manual-quote-shipping manual-quote-shipping--wizard mt-2">
                    <div className="row">
                      <div className="col-12 col-xl-10 mx-auto">
                        {addressRowsForShipping.length === 0 ? (
                          <>
                            <div className="manual-quote-inline-address px-sm-4">
                              <NewAddress
                                ref={firstAddressWizardRef}
                                key={firstAddressFormKey}
                                wizardInline
                                onInlineBlockingLoadChange={setInlineAddressBlockingLoad}
                                handleClose={() => {}}
                                initialLoactionType={initialLoactionTypeForModal}
                                onAddressCreated={(newId: string | number, created?: any) => {
                                  const idStr = String(newId);
                                  const instr = String(
                                    created?.instructions ?? created?.delivery_instructions ?? "",
                                  ).trim();
                                  const fromApi = shippingIdFromAddressCreateResponse(created);
                                  setShippingData((s) => ({
                                    ...s,
                                    address_id: idStr,
                                    shipping_id: fromApi || s.shipping_id,
                                  }));
                                  setShippingMeta((m) => ({
                                    ...m,
                                    delivery_instructions: instr || m.delivery_instructions,
                                  }));
                                  addressListShipping.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
                                  setFirstAddressFormKey((k) => k + 1);
                                }}
                              />
                            </div>
                            {shippingErrors.address || shippingErrors.shipping ? (
                              <div className="mt-2">
                                {shippingErrors.address ? (
                                  <p className="text-danger small mb-1">{shippingErrors.address}</p>
                                ) : null}
                                {shippingErrors.shipping ? (
                                  <p className="text-danger small mb-0">{shippingErrors.shipping}</p>
                                ) : null}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div className="manual-quote-shipping__title-row">
                              <h3 className="manual-quote-shipping__section-title mb-0">
                                {LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION}
                              </h3>
                            </div>
                            <div className="manual-quote-shipping__radios">
                              {addressRowsForShipping.map((addr: any) => (
                                <div key={addr.id} className="form-check">
                                  <input
                                    type="radio"
                                    id={`ship-address-${addr.id}`}
                                    name="ship_address"
                                    className="form-check-input"
                                    value={addr.id}
                                    checked={String(shippingData.address_id) === String(addr.id)}
                                    onChange={() => {
                                      const idStr = String(addr.id);
                                      setShowInlineNewAddressForm(false);
                                      setShippingData((s) => ({ ...s, address_id: idStr, shipping_id: "" }));
                                      setShippingMeta((m) => ({
                                        ...m,
                                        delivery_instructions: deliveryInstructionsFromAddressRow(addr),
                                      }));
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`ship-address-${addr.id}`}>
                                    {formatAddressListRow(addr)}
                                  </label>
                                </div>
                              ))}
                            {shippingErrors.address ? (
                              <div>
                                <p className="text-danger small mb-0">{shippingErrors.address}</p>
                              </div>
                            ) : null}
                            </div>
                            {!showInlineNewAddressForm ? (
                                <div className="manual-quote-add-address">
                                  <button
                                    type="button"
                                    className="manual-quote-add-btn com-btn px-3"
                                    onClick={() => {
                                      setAddressModalKey((k) => k + 1);
                                      setShowInlineNewAddressForm(true);
                                    }}
                                  >
                                   {LanguageConstants?.NEWADDRES}
                                  </button>
                                </div>
                              ) : null}

                            {showInlineNewAddressForm ? (
                              <div className="manual-quote-inline-address manual-quote-inline-address--between px-sm-4">
                                <NewAddress
                                  key={`inline-new-addr-${addressModalKey}`}
                                  wizardInline
                                  showSubmitInForm
                                  showCancelInForm
                                  hideShipMethodAndPrimaryInForm
                                  submitLabel={LanguageConstants?.CONFIRMA ?? "Confirm"}
                                  onInlineBlockingLoadChange={setInlineAddressBlockingLoad}
                                  handleClose={() => setShowInlineNewAddressForm(false)}
                                  initialLoactionType={initialLoactionTypeForModal}
                                  onAddressCreated={(newId: string | number, created?: any) => {
                                    const idStr = String(newId);
                                    const instr = String(
                                      created?.instructions ?? created?.delivery_instructions ?? "",
                                    ).trim();
                                    const fromApi = shippingIdFromAddressCreateResponse(created);
                                    setShippingData((s) => ({
                                      ...s,
                                      address_id: idStr,
                                      shipping_id: fromApi || s.shipping_id,
                                    }));
                                    setShippingMeta((m) => ({
                                      ...m,
                                      delivery_instructions: instr || m.delivery_instructions,
                                    }));
                                    setShowInlineNewAddressForm(false);
                                    addressListShipping.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
                                  }}
                                />
                              </div>
                            ) : null}

                            <div className="manual-quote-shipping__divider" />

                            <h3 className="manual-quote-shipping__section-title">{LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION}</h3>
                            <div>
                              <p className="manual-quote-shipping__label">{LanguageConstants?.SHIPPINGMETHOD}</p>
                              <div className="manual-quote-shipping__radio-row mb-2">
                                {wizardShippingRows.length > 0 ? (
                                  wizardShippingRows.map((s) => (
                                    <div key={s.methodId} className="form-check">
                                    <input
                                      type="radio"
                                      id={`ship-method-${s.refId}`}
                                      name="ship_method"
                                      className="form-check-input"
                                      value={s.refId}
                                      checked={
                                        String(shippingData.shipping_id) === s.refId ||
                                        String(shippingData.shipping_id) === s.methodId
                                      }
                                      onChange={() => setShippingData((d) => ({ ...d, shipping_id: s.refId }))}
                                    />
                                    <label className="form-check-label" htmlFor={`ship-method-${s.refId}`}>
                                      {s.label}
                                    </label>
                                  </div>
                                  ))
                                ) : null}
                              </div>
                              {shippingErrors.shipping ? (
                                <p className="text-danger small mb-2">{shippingErrors.shipping}</p>
                              ) : null}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="manual-quote-footer manual-quote-footer--step">
                      <button type="button" className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill" onClick={() => setStep(1)}>
                        {LanguageConstants?.BACK}
                      </button>
                      <span
                        className={showInlineNewAddressForm ? "d-inline-block" : undefined}
                        title={
                          showInlineNewAddressForm
                            ? getFromStorage({ key: StorageConstant?.Translate }) === "spa"
                              ? "Cierre la sección de nueva dirección para continuar"
                              : "Close the new address section to continue"
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          className="manual-quote-btn com-btn rounded-pill"
                          onClick={handleShippingNext}
                          disabled={showInlineNewAddressForm}
                          style={showInlineNewAddressForm ? { pointerEvents: "none" } : undefined}
                        >
                          {LanguageConstants?.REVIEW}
                        </button>
                      </span>
                    </div>
                  </div>
                )}

                {!isAddProduct && step === 3 && (
                  <div className={`manual-quote-review mt-2${gridView ? " manual-quote-review--is-grid" : " manual-quote-review--is-list"}`}>
                    <div className="manual-quote-review__layout">
                      <div className="manual-quote-review__main">
                        {!gridView ? (
                          <div className="manual-quote-review-list">
                            {reviewQuotes.map((row: any, i: number) => (
                              <div className="manual-quote-review-list__item" key={i}>
                                <dl className="manual-quote-review-list__dl">
                                  {/** Keep list-view heading aligned with design: Item N + qty on the first row. */}
                                  <div className="d-sm-flex justify-content-between gap-2">
                                    <div>
                                      <div className="manual-quote-review-list__row--heading mb-2">
                                        <dt>{`${LanguageConstants?.Item} ${state?.quotes_length ? state?.quotes_length + i + 1 : i + 1}`}</dt>
                                      </div>
                                      <div className="manual-quote-review-list__row manual-quote-review-list__row--headline">
                                        <dt>{reviewHeadline(row)}</dt>
                                      </div>
                                      <div className="manual-quote-review-list__row">
                                        <dt>{LanguageConstants?.MANUAL_QUOTE_REVIEW_PART_NUMBER} :</dt>
                                        <dd>{reviewDisplay(row.part_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</dd>
                                      </div>
                                      <div className="manual-quote-review-list__row">
                                        <dt>{LanguageConstants?.MANUAL_QUOTE_REVIEW_SERIAL_NUMBER} :</dt>
                                        <dd>{reviewDisplay(row.serial_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</dd>
                                      </div>
                                      <div className="manual-quote-review-list__row">
                                        <dt>{LanguageConstants?.MODELNUMBER} :</dt>
                                        <dd>{reviewDisplay(row.model_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</dd>
                                      </div>
                                      <div className="manual-quote-review-list__row">
                                        <dt>{LanguageConstants?.SKU_LABEL} :</dt>
                                        <dd>{reviewDisplay(row.sku, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</dd>
                                      </div>
                                      <div className="manual-quote-review-list__row">
                                        <dt>{LanguageConstants?.Description} :</dt>
                                        <dd>{reviewDisplay(row.description, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</dd>
                                      </div>
                                    </div>
                                    <div className="mt-auto manual-quote-review-list__row mb-0">
                                      <dt>{`${LanguageConstants?.QTY_SHORT} ${row.quantity}`}</dt>
                                    </div>
                                  </div>
                                </dl>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="manual-quote-table-wrap manual-quote-review-table-wrap">
                            <table className="table manual-quote-table manual-quote-review-table mb-0">
                              <thead>
                                <tr>
                                  <th>{LanguageConstants?.Item}</th>
                                  <th>{LanguageConstants?.BRANDNAME}</th>
                                  <th>{LanguageConstants?.MANUAL_QUOTE_REVIEW_PART_NUMBER}</th>
                                  <th>{LanguageConstants?.QTY_SHORT}</th>
                                  <th>{LanguageConstants?.Description}</th>
                                  <th>{LanguageConstants?.MANUAL_QUOTE_REVIEW_SERIAL_NUMBER}</th>
                                  <th>{LanguageConstants?.MODELNUMBER}</th>
                                  <th>{LanguageConstants?.SKU_LABEL}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reviewQuotes.map((row: any, i: number) => (
                                  <tr key={i}>
                                    <td>{state?.quotes_length ? state?.quotes_length + i + 1 : i + 1}</td>
                                    <td>{brandLabelForRow(row)}</td>
                                    <td>{reviewDisplay(row.part_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</td>
                                    <td>{row.quantity}</td>
                                    <td>{reviewDisplay(row.description, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</td>
                                    <td>{reviewDisplay(row.serial_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</td>
                                    <td>{reviewDisplay(row.model_number, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</td>
                                    <td>{reviewDisplay(row.sku, LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                      <aside className="manual-quote-review__aside">
                        <div className="manual-quote-ship-card">
                          <div className="manual-quote-ship-card__head">
                            <img src={truckIcon} alt="truck" className="manual-quote-ship-card__icon" />
                            {LanguageConstants?.MANUAL_QUOTE_SHIPPING_DETAILS}
                          </div>
                          <div className="manual-quote-ship-card__body">
                            <p>
                              <strong>{LanguageConstants?.DELADDRESS}</strong>
                              {selectedAddressRow
                                ? formatAddressListRow(selectedAddressRow)
                                : "—"}
                            </p>
                            {shippingMeta.delivery_instructions ? (
                              <p>
                                <strong>{LanguageConstants?.MANUAL_QUOTE_INSTRUCTIONS}</strong>
                                {shippingMeta.delivery_instructions}
                              </p>
                            ) : null}
                            {reviewResidenceLabel ? (
                              <p>
                                <strong>{LanguageConstants?.MANUAL_QUOTE_RESIDENCE}</strong>
                                {reviewResidenceLabel}
                              </p>
                            ) : null}
                            <p>
                              <strong>{LanguageConstants?.SHIPINNG}</strong>
                              {selectedShippingLabel}
                            </p>
                            {reviewBillingSame ? (
                              <p className="mb-0 text-muted small">{LanguageConstants?.MANUAL_QUOTE_BILLING_SAME}</p>
                            ) : null}
                          </div>
                        </div>
                      </aside>
                    </div>
                    <div className="manual-quote-footer manual-quote-footer--step">
                      <button type="button" className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill" onClick={() => setStep(2)}>
                        {LanguageConstants?.BACK}
                      </button>
                      <button
                        type="button"
                        className="manual-quote-btn com-btn rounded-pill"
                        onClick={submitFinalQuote}
                        disabled={showSubmitLoader}
                        aria-busy={showSubmitLoader}
                      >
                        {LanguageConstants?.REQUEST_A_QUOTE}
                      </button>
                    </div>
                  </div>
                )}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

    </>
  )
}

export default RequestQuote